export const DEFAULT_ACCOUNT_TYPES = ['account', 'subaccount'];

/**
 * Single source of truth deciding whether a menu-shaped item may be shown to
 * the current user. Used by the settings submenu (Secondary.vue) and by the
 * Applications screen cards, so that moving a screen from the menu into
 * Applications never widens or narrows who can reach it.
 *
 * The evaluation order mirrors the historical behaviour and must be preserved:
 * `opportunityPipelinesManagement` short-circuits before `featureFlag`.
 *
 * @param {Object} item menu item / application card descriptor
 * @param {Object} context resolved store getters for the current session
 * @returns {Boolean}
 */
export const isMenuItemAccessible = (item, context = {}) => {
  const {
    currentRole,
    typeAccount,
    currentUser = {},
    isOnChatwootCloud = false,
    globalConfig = {},
    accountId,
    isFeatureEnabledonAccount,
    isOpportunityPipelinesManagementAllowed,
    roleWiseRoutes = window.roleWiseRoutes,
  } = context;

  if (!item || !currentRole) {
    return false;
  }

  const accessibleRoutes = (roleWiseRoutes || {})[currentRole];
  if (!accessibleRoutes || !accessibleRoutes.includes(item.toStateName)) {
    return false;
  }

  if (item.showOnlyOnCloud && !isOnChatwootCloud) {
    return false;
  }

  if (item.userTypes && item.userTypes.length) {
    if (!item.userTypes.includes(currentUser.type)) {
      return false;
    }
  }

  const accountTypes = item.accountTypes ?? DEFAULT_ACCOUNT_TYPES;
  if (!accountTypes.includes(typeAccount)) {
    return false;
  }

  if (item.globalConfigFlag) {
    return !!globalConfig[item.globalConfigFlag];
  }

  if (item.opportunityPipelinesManagement) {
    return !!(
      isOpportunityPipelinesManagementAllowed &&
      isOpportunityPipelinesManagementAllowed(accountId)
    );
  }

  if (item.featureFlag) {
    return !!(
      isFeatureEnabledonAccount &&
      isFeatureEnabledonAccount(accountId, item.featureFlag)
    );
  }

  return true;
};

export default isMenuItemAccessible;

/**
 * `$i18n.locale` uses underscores (e.g. "pt_BR"), but `Intl`/`localeCompare`
 * expect BCP-47 tags with hyphens (e.g. "pt-BR") and throw a RangeError
 * otherwise. Normalize before handing the locale to either API.
 */
export const toBCP47Locale = locale => (locale || '').replace(/_/g, '-');
